/**
 * Enhances variant stock accessibility without touching theme.js
 */
(() => {
  const STATUS_CLASSES = ['count-is-in', 'count-is-preorder', 'count-is-low', 'count-is-out', 'count-is-unavailable'];
  const ESSENTIAL_SELECTOR =
    '.essential-preorder-container-active, [data-essential-preorder-element], .essential-preorder-hidden-input';

  const onReady = (cb) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', cb);
    } else {
      cb();
    }
  };

  const parseJSON = (element) => {
    if (!element) return null;
    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      console.warn('inventory-accessibility: failed to parse JSON', error);
      return null;
    }
  };

  const normalizeRemaining = (value) => {
    if (value === null || typeof value === 'undefined') return null;
    if (typeof value === 'number') return value;

    const trimmedValue = String(value).trim();
    if (trimmedValue === '') return null;

    const numericValue = Number(trimmedValue);
    if (!Number.isNaN(numericValue)) {
      return numericValue;
    }
    return trimmedValue;
  };

  const buildStatusText = (strings, remaining, variant) => {
    if (!strings) return '';
    const normalized = normalizeRemaining(remaining);

    if (normalized === 'out' || normalized === 0 || variant?.available === false) {
      return strings.out || strings.unavailable || '';
    }

    if (normalized === 'in' || normalized === null) {
      return variant?.available === false ? strings.out || strings.unavailable || '' : strings.in || strings.low || '';
    }

    if (normalized === 'low') {
      return strings.low || strings.lowWithCount || strings.in || '';
    }

    if (typeof normalized === 'number') {
      if (normalized <= 0) {
        return strings.out || strings.unavailable || '';
      }
      if (strings.lowWithCount) {
        return strings.lowWithCount.replace('__COUNT__', normalized);
      }
      return strings.low || strings.in || '';
    }

    return strings.unavailable || '';
  };

  const updateListItems = (items, variantId, text) => {
    items.forEach((item) => {
      const matches = Number(item.getAttribute('data-variant-id')) === variantId;
      if (matches) {
        item.setAttribute('data-selected', 'true');
        const statusNode = item.querySelector('[data-variant-stock-status]');
        if (statusNode) statusNode.textContent = text;
      } else {
        item.removeAttribute('data-selected');
      }
    });
  };

  onReady(() => {
    const productComponents = document.querySelectorAll('product-component');
    if (!productComponents.length) return;

    productComponents.forEach((component) => {
      if (component.dataset.stockAccessibilityInit === 'true') return;

      const stockList = component.querySelector('[data-variant-stock-list]');
      const productJSONEl = component.querySelector('[data-product-json]');

      if (!stockList || !productJSONEl) return;

      const productData = parseJSON(productJSONEl);
      if (!productData) return;

      component.dataset.stockAccessibilityInit = 'true';

      const statusEl = component.querySelector('[data-variant-accessibility-status]');
      const variantSelect = component.querySelector('[data-product-select]');
      const remainingJSONEl = component.querySelector('[data-product-remaining-json]');
      const remainingWrapper = component.querySelector('[data-remaining-wrapper]');
      const productForm = component.querySelector('form[action*="/cart/add"]');

      let remainingData = parseJSON(remainingJSONEl);
      let currentVariant = null;
      let statusFrame = null;
      const stockItems = Array.from(stockList.querySelectorAll('[data-variant-stock]'));
      const strings = {
        in: stockList.dataset.stockTextIn || '',
        preorder: stockList.dataset.stockTextPreorder || '',
        out: stockList.dataset.stockTextOut || '',
        low: stockList.dataset.stockTextLow || '',
        lowWithCount: stockList.dataset.stockTextLowWithCount || '',
        unavailable: stockList.dataset.stockTextUnavailable || '',
      };

      const getRemainingForVariant = (variantId) => {
        if (!remainingData) return null;
        const key = String(variantId);
        return typeof remainingData[key] !== 'undefined' ? remainingData[key] : null;
      };

      const getStandardStatusClass = (variant) => {
        if (!variant) return 'count-is-unavailable';

        const normalized = normalizeRemaining(getRemainingForVariant(variant.id));
        const maxInventory = Number(remainingWrapper?.dataset.remainingMax);

        if (normalized === 'out' || (typeof normalized === 'number' && normalized < 1)) {
          return 'count-is-out';
        }
        if (
          normalized === 'in' ||
          (typeof normalized === 'number' && !Number.isNaN(maxInventory) && normalized >= maxInventory)
        ) {
          return 'count-is-in';
        }
        if (
          normalized === 'low' ||
          (typeof normalized === 'number' && normalized > 0 && normalized < maxInventory)
        ) {
          return 'count-is-low';
        }

        return variant.available === false ? 'count-is-out' : 'count-is-in';
      };

      const hasActiveEssentialPreorder = () => {
        if (!productForm) return false;

        const campaignId = productForm.querySelector(
          'input[name="properties[_essential_preorder_campaign_id]"]'
        )?.value;
        const preorderButton = productForm.querySelector(
          '.essential-preorder-container-active [data-essential-preorder-element="button"]'
        );

        return Boolean(campaignId && preorderButton);
      };

      const updateVisibleStatus = (variant, isPreorder) => {
        if (!remainingWrapper) return;

        const statusClass = isPreorder ? 'count-is-preorder' : getStandardStatusClass(variant);
        remainingWrapper.classList.remove(...STATUS_CLASSES);
        remainingWrapper.classList.add(statusClass);
      };

      const applyStatus = (variant) => {
        const isPreorder = Boolean(variant && hasActiveEssentialPreorder());
        updateVisibleStatus(variant, isPreorder);

        if (!variant) {
          if (statusEl) statusEl.textContent = strings.unavailable;
          stockItems.forEach((item) => item.removeAttribute('data-selected'));
          return;
        }

        const remainingValue = getRemainingForVariant(variant.id);
        const statusText = isPreorder
          ? strings.preorder
          : buildStatusText(strings, remainingValue, variant);

        updateListItems(stockItems, variant.id, statusText);

        if (statusEl) {
          const label = variant.public_title || variant.title || productData.title || '';
          statusEl.textContent = label ? `${label}: ${statusText}` : statusText;
        }
      };

      const resolveVariantById = (variantId) => {
        if (!variantId || !productData.variants) return null;
        const numericId = Number(variantId);
        return productData.variants.find((variant) => Number(variant.id) === numericId) || null;
      };

      const handleVariantChange = (variant) => {
        currentVariant = variant;
        updateVisibleStatus(variant, false);

        if (statusFrame !== null) cancelAnimationFrame(statusFrame);
        statusFrame = requestAnimationFrame(() => {
          statusFrame = null;
          applyStatus(currentVariant);
        });
      };

      component.addEventListener('theme:variant:change', (event) => {
        const variantFromEvent = event.detail?.variant || null;
        handleVariantChange(variantFromEvent);
      });

      const initialVariant = variantSelect ? resolveVariantById(variantSelect.value) : productData.variants?.[0];
      handleVariantChange(initialVariant);

      if (remainingJSONEl) {
        const observer = new MutationObserver(() => {
          remainingData = parseJSON(remainingJSONEl) || remainingData;
        });
        observer.observe(remainingJSONEl, {characterData: true, subtree: true});
      }

      if (productForm) {
        const essentialObserver = new MutationObserver((mutations) => {
          const essentialChanged = mutations.some((mutation) => {
            if (mutation.type === 'attributes') {
              return mutation.target.matches?.(ESSENTIAL_SELECTOR);
            }

            return [...mutation.addedNodes, ...mutation.removedNodes].some(
              (node) =>
                node.nodeType === Node.ELEMENT_NODE &&
                (node.matches?.(ESSENTIAL_SELECTOR) || node.querySelector?.(ESSENTIAL_SELECTOR))
            );
          });

          if (essentialChanged) handleVariantChange(currentVariant);
        });

        essentialObserver.observe(productForm, {
          attributes: true,
          attributeFilter: ['class'],
          childList: true,
          subtree: true,
        });
      }
    });
  });
})();

